// frontend/src/components/PushNotificationManager.jsx
// Push notification manager for browser notifications

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNotifications } from "../contexts/NotificationContext";
import { getApiBaseUrl } from "../config/api.js";

const PushNotificationManager = ({
  children,
  className = "",
  autoRequestPermission = false,
  enableSound = true,
  enableVibration = true,
  defaultIcon = "/icon-192x192.png",
  defaultBadge = "/badge-72x72.png",
  showDebugPanel = false, // New prop to control debug panel visibility
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError, showInfo } = useNotifications();

  const [permission, setPermission] = useState("default");
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  // Get JWT token for authenticated API calls
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    }
    return { "Content-Type": "application/json" };
  }, []);

  // Fetch VAPID public key from backend
  useEffect(() => {
    const fetchVapidKey = async () => {
      try {
        const apiUrl = `${getApiBaseUrl()}/api/push/vapid-public-key`;
        console.log("[PushNotificationManager] Fetching VAPID key from:", apiUrl);
        
        const response = await fetch(apiUrl);
        console.log("[PushNotificationManager] VAPID key response status:", response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log("[PushNotificationManager] VAPID key response data:", data);
          
          if (data.success && data.vapid_public_key) {
            // Store the base64url string directly (needed for subscription)
            setVapidPublicKey(data.vapid_public_key);
            console.log("[PushNotificationManager] VAPID key set successfully");
          } else {
            console.warn("[PushNotificationManager] VAPID key response missing success or key:", data);
          }
        } else {
          const errorText = await response.text();
          console.error("[PushNotificationManager] Failed to fetch VAPID key:", response.status, errorText);
        }
      } catch (error) {
        console.error("[PushNotificationManager] Error fetching VAPID public key:", error);
      }
    };

    fetchVapidKey();
  }, []);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = "serviceWorker" in navigator && "PushManager" in window;
      setIsSupported(supported);

      if (supported) {
        setPermission(Notification.permission);
      }
    };

    checkSupport();
  }, []);

  // Restore existing subscription on mount
  useEffect(() => {
    const restoreSubscription = async () => {
      if (!isSupported || permission !== "granted") {
        return;
      }

      try {
        // Check if service worker is ready
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          
          // Get existing subscription from PushManager
          const existingSubscription = await registration.pushManager.getSubscription();
          
          if (existingSubscription) {
            console.log("[PushNotificationManager] Found existing subscription, restoring...");
            setSubscription(existingSubscription);
            
            // Optionally verify with backend
            try {
              const token = localStorage.getItem("jwtToken");
              if (!token) {
                console.log("[PushNotificationManager] No JWT token available; skipping backend subscription verification");
                return;
              }

              const headers = {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              };

              const response = await fetch(`${getApiBaseUrl()}/api/push/subscriptions`, {
                method: 'GET',
                headers,
              });
              
              if (response.ok) {
                const data = await response.json();
                if (data.success && data.subscriptions && data.subscriptions.length > 0) {
                  // Verify the subscription endpoint matches
                  const backendSub = data.subscriptions.find(
                    sub => sub.endpoint === existingSubscription.endpoint
                  );
                  if (backendSub) {
                    console.log("[PushNotificationManager] Subscription verified with backend");
                  } else {
                    console.warn("[PushNotificationManager] Subscription not found in backend, may need to re-subscribe");
                  }
                }
              } else if (response.status === 401) {
                console.log("[PushNotificationManager] Backend subscription check skipped due to unauthorized response");
              }
            } catch (error) {
              console.warn("[PushNotificationManager] Could not verify subscription with backend:", error);
            }
          }
        }
      } catch (error) {
        console.error("[PushNotificationManager] Error restoring subscription:", error);
      }
    };

    if (isSupported && permission === "granted") {
      restoreSubscription();
    }
  }, [isSupported, permission, getAuthHeaders]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      showError(t("push_notifications.push_notifications_not_supported"));
      return false;
    }

    try {
      setIsLoading(true);
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === "granted") {
        showSuccess(t("push_notifications.push_notifications_enabled"));
        return true;
      } else {
        showError(t("push_notifications.push_notifications_denied"));
        return false;
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      showError(t("push_notifications.push_notification_permission_failed"));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, showSuccess, showError, t]);

  // Helper function to convert ArrayBuffer to base64
  const arrayBufferToBase64 = useCallback((buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }, []);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(
    async (keyOverride = null) => {
      if (!isSupported || permission !== "granted") {
        showError(t("push_notifications.push_notifications_not_available"));
        return null;
      }

      // Use provided key or the one from backend
      const keyToUse = keyOverride || vapidPublicKey;
      if (!keyToUse) {
        showError(t("push_notifications.vapid_key_not_available") || "VAPID key not available");
        return null;
      }

      try {
        setIsLoading(true);

        // Register service worker if not already registered
        console.log("[PushNotificationManager] Registering service worker...");
        const registration = await navigator.serviceWorker.register(
          "/service-worker.js"
        );
        console.log("[PushNotificationManager] Service worker registered, waiting for ready...");

        // Wait for service worker to be ready/active
        await registration.update();
        const readyRegistration = await navigator.serviceWorker.ready;
        console.log("[PushNotificationManager] Service worker ready, state:", readyRegistration.active?.state);

        if (!readyRegistration.active) {
          throw new Error("Service worker is not active. Please refresh the page and try again.");
        }

        // Convert base64url string to Uint8Array
        // VAPID keys are stored as base64url (64 bytes for uncompressed public key without 0x04 prefix)
        // But the browser PushManager expects the full 65-byte uncompressed point WITH 0x04 prefix
        let applicationServerKey;
        if (typeof keyToUse === 'string') {
          console.log("[PushNotificationManager] Converting VAPID key from base64url, original length:", keyToUse.length);
          
          // Convert base64url to base64 (replace - with +, _ with /)
          const base64 = keyToUse.replace(/-/g, '+').replace(/_/g, '/');
          
          // Add padding if needed
          const padding = (4 - (base64.length % 4)) % 4;
          const padded = base64 + '='.repeat(padding);
          
          // Decode base64 to binary string
          const binaryString = atob(padded);
          
          // Convert binary string to Uint8Array
          const keyBytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            keyBytes[i] = binaryString.charCodeAt(i);
          }
          
          // The browser expects the full uncompressed EC point (65 bytes with 0x04 prefix)
          // Our key is 64 bytes (without prefix), so we need to add 0x04 at the beginning
          applicationServerKey = new Uint8Array(65);
          applicationServerKey[0] = 0x04; // Uncompressed point prefix
          applicationServerKey.set(keyBytes, 1); // Copy the 64 bytes after the prefix
          
          console.log("[PushNotificationManager] VAPID key converted, byte length:", applicationServerKey.length, "(expected 65)");
        } else {
          applicationServerKey = keyToUse;
        }

        console.log("[PushNotificationManager] Subscribing to push notifications...");

        // Subscribe to push notifications using the ready registration
        const pushSubscription = await readyRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey,
        });
        
        console.log("[PushNotificationManager] Successfully subscribed to push notifications");

        // Convert subscription to format for backend
        const subscriptionData = {
          endpoint: pushSubscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(pushSubscription.getKey('p256dh')),
            auth: arrayBufferToBase64(pushSubscription.getKey('auth')),
          },
        };

        // Save subscription to backend
        const headers = getAuthHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/push/subscribe`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(subscriptionData),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
        setSubscription(pushSubscription);
        showSuccess(t("push_notifications.push_subscription_successful"));
        return pushSubscription;
          } else {
            throw new Error(result.error || "Failed to save subscription");
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("Error subscribing to push notifications:", error);
        showError(t("push_notifications.push_subscription_failed") + ": " + error.message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isSupported, permission, vapidPublicKey, showSuccess, showError, t, getAuthHeaders, arrayBufferToBase64]
  );

  // Unsubscribe from push notifications
  const unsubscribeFromPush = useCallback(async () => {
    if (!subscription) {
      showInfo(t("push_notifications.no_active_subscription"));
      return false;
    }

    try {
      setIsLoading(true);

      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Remove subscription from backend
      const headers = getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/api/push/unsubscribe`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });

      if (response.ok) {
        setSubscription(null);
        showSuccess(t("push_notifications.push_unsubscription_successful"));
        return true;
      } else {
        // Even if backend call fails, we've unsubscribed locally
        console.warn("Failed to remove subscription from backend:", response.status);
      setSubscription(null);
      showSuccess(t("push_notifications.push_unsubscription_successful"));
      return true;
      }
    } catch (error) {
      console.error("Error unsubscribing from push notifications:", error);
      showError(t("push_notifications.push_unsubscription_failed"));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [subscription, showSuccess, showError, showInfo, t, getAuthHeaders]);

  // Send local notification
  const sendLocalNotification = useCallback(
    (title, options = {}) => {
      if (!isSupported || permission !== "granted") {
        showError(t("push_notifications.push_notifications_not_available"));
        return false;
      }

      try {
        const notification = new Notification(title, {
          icon: defaultIcon,
          badge: defaultBadge,
          requireInteraction: false,
          silent: !enableSound,
          vibrate: enableVibration ? [200, 100, 200] : undefined,
          ...options,
        });

        // Auto-close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);

        return true;
      } catch (error) {
        console.error("Error sending local notification:", error);
        showError(t("push_notifications.local_notification_failed"));
        return false;
      }
    },
    [
      isSupported,
      permission,
      defaultIcon,
      defaultBadge,
      enableSound,
      enableVibration,
      showError,
      t,
    ]
  );

  // Send notification through service worker
  const sendServiceWorkerNotification = useCallback(
    async (title, options = {}) => {
      if (!isSupported || permission !== "granted") {
        showError(t("push_notifications.push_notifications_not_available"));
        return false;
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration();

        if (registration && registration.active) {
          registration.active.postMessage({
            type: "SHOW_NOTIFICATION",
            title,
            options: {
              icon: defaultIcon,
              badge: defaultBadge,
              requireInteraction: false,
              silent: !enableSound,
              vibrate: enableVibration ? [200, 100, 200] : undefined,
              ...options,
            },
          });
          return true;
        } else {
          // Fallback to local notification
          return sendLocalNotification(title, options);
        }
      } catch (error) {
        console.error("Error sending service worker notification:", error);
        return sendLocalNotification(title, options);
      }
    },
    [
      isSupported,
      permission,
      defaultIcon,
      defaultBadge,
      enableSound,
      enableVibration,
      sendLocalNotification,
      showError,
      t,
    ]
  );

  // Test notification - send via backend push API
  const sendTestNotification = useCallback(async () => {
    console.log("[PushNotificationManager] sendTestNotification called, subscription:", subscription ? "exists" : "null");
    
    if (!subscription) {
      console.warn("[PushNotificationManager] No subscription found, cannot send test notification");
      showError("Please subscribe to push notifications first");
      return false;
    }

    try {
      console.log("[PushNotificationManager] Sending test notification request...");
      setIsLoading(true);
      const headers = getAuthHeaders();
      const apiUrl = `${getApiBaseUrl()}/api/push/test`;
      console.log("[PushNotificationManager] Calling:", apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          title: "Test Notification",
          body: "This is a test push notification from Alertique!"
        }),
      });

      console.log("[PushNotificationManager] Test notification response status:", response.status);

      if (response.ok) {
        const result = await response.json();
        console.log("[PushNotificationManager] Test notification response:", result);
        if (result.success) {
          showSuccess(`Test notification sent to ${result.result?.success_count || 0} device(s)`);
          return true;
        } else {
          throw new Error(result.error || "Failed to send test notification");
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("[PushNotificationManager] Test notification failed:", response.status, errorData);
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error("[PushNotificationManager] Error sending test notification:", error);
      showError(`Failed to send test notification: ${error.message}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [subscription, getAuthHeaders, showSuccess, showError]);

  // Auto-request permission if enabled
  useEffect(() => {
    if (autoRequestPermission && isSupported && permission === "default") {
      requestPermission();
    }
  }, [autoRequestPermission, isSupported, permission, requestPermission]);

  // Get subscription info
  const getSubscriptionInfo = useCallback(() => {
    if (!subscription) return null;

    return {
      endpoint: subscription.endpoint,
      keys: subscription.keys
        ? {
            p256dh: subscription.keys.p256dh,
            auth: subscription.keys.auth,
          }
        : null,
    };
  }, [subscription]);

  // Check if subscription is active
  const isSubscribed = useCallback(() => {
    return subscription !== null;
  }, [subscription]);

  const contextValue = {
    // State
    permission,
    subscription,
    isSupported,
    isLoading,
    isSubscribed: isSubscribed(),

    // Actions
    requestPermission,
    subscribeToPush,
    unsubscribeFromPush,
    sendLocalNotification,
    sendServiceWorkerNotification,
    sendTestNotification,
    getSubscriptionInfo,
  };

  return (
    <div className={`push-notification-manager ${className}`}>
      {children}

      {/* Debug panel (optional) */}
      {showDebugPanel && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white p-4 rounded-lg text-xs max-w-80">
          <h3 className="font-bold mb-2">Push Notifications</h3>
          <div className="space-y-1">
            <div>Supported: {isSupported ? "Yes" : "No"}</div>
            <div>Permission: {permission}</div>
            <div>Subscribed: {isSubscribed() ? "Yes" : "No"}</div>
            <div>Loading: {isLoading ? "Yes" : "No"}</div>
          </div>

          <div className="mt-3 space-y-1">
            {permission === "default" && (
              <button
                onClick={requestPermission}
                disabled={isLoading}
                className="block w-full bg-blue-600 px-2 py-1 rounded text-xs disabled:opacity-50"
              >
                Request Permission
              </button>
            )}

            {permission === "granted" && !isSubscribed() && (
              <button
                onClick={() => subscribeToPush()}
                disabled={isLoading || !vapidPublicKey}
                className="block w-full bg-green-600 px-2 py-1 rounded text-xs disabled:opacity-50"
              >
                Subscribe
              </button>
            )}

            {isSubscribed() && (
              <>
              <button
                onClick={unsubscribeFromPush}
                disabled={isLoading}
                className="block w-full bg-red-600 px-2 py-1 rounded text-xs disabled:opacity-50"
              >
                Unsubscribe
              </button>
            <button
                  onClick={() => {
                    console.log("[PushNotificationManager] Test Notification button clicked, subscription:", subscription ? "exists" : "null");
                    sendTestNotification();
                  }}
                  disabled={isLoading || !subscription}
              className="block w-full bg-purple-600 px-2 py-1 rounded text-xs disabled:opacity-50"
            >
              Test Notification
            </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PushNotificationManager;
